import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import QRCode from "qrcode";
import { apiGet } from "../../api/http";

type CheckInBoardActivity = {
  id: string;
  title: string;
  location: string;
  eventDate: string;
  status: string;
  capacity: number;
  enrolled: number;
  checkedIn: number;
  participationPoints: number;
};

type CheckInBoardAttendance = {
  memberName: string;
  memberNo: string;
  pointsAwarded: number;
  checkedAt: string;
};

type CheckInBoardResponse = {
  activity: CheckInBoardActivity;
  qrPayload: string;
  qrRotateSeconds: number;
  attendances: CheckInBoardAttendance[];
};

const AVATAR_HUES = [
  "bg-red-500",
  "bg-amber-500",
  "bg-teal-500",
  "bg-green-500",
  "bg-rose-500",
  "bg-orange-500",
  "bg-sky-500",
  "bg-indigo-500",
];

export default function ActivityCheckinPage() {
  const { activityId } = useParams<{ activityId: string }>();
  const [searchParams] = useSearchParams();
  const tenant = searchParams.get("tenant")?.trim() ?? "";

  const [board, setBoard] = useState<CheckInBoardResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [lastSync, setLastSync] = useState<Date | null>(null);

  const fetchBoard = useCallback(async () => {
    if (!tenant || !activityId) return;
    try {
      const data = await apiGet<CheckInBoardResponse>(
        `/api/tenants/${encodeURIComponent(tenant)}/activities/${encodeURIComponent(activityId)}/check-in-board`,
      );
      setBoard(data);
      setLoadError(null);
      setLastSync(new Date());
    } catch {
      setLoadError("无法加载签到看板，请检查活动是否存在、租户参数是否正确或网络连接。");
      setBoard(null);
    }
  }, [tenant, activityId]);

  useEffect(() => {
    void fetchBoard();
    const id = window.setInterval(() => void fetchBoard(), 3000);
    return () => window.clearInterval(id);
  }, [fetchBoard]);

  useEffect(() => {
    const p = board?.qrPayload;
    if (!p) {
      setQrDataUrl("");
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(p, {
      width: 360,
      margin: 2,
      errorCorrectionLevel: "M",
      color: { dark: "#0f172a", light: "#ffffff" },
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl("");
      });
    return () => {
      cancelled = true;
    };
  }, [board?.qrPayload]);

  if (!activityId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 p-6 text-center text-white">
        <p>缺少活动参数</p>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-950 p-6 text-center">
        <i className="ri-qr-code-line text-5xl text-slate-600" />
        <p className="text-lg font-semibold text-white">缺少租户参数</p>
        <p className="max-w-sm text-sm text-slate-400">
          请在管理后台「活动列表」中点击「签到码」打开本页；链接需包含 <code className="text-red-300">?tenant=</code>{" "}
          查询参数。
        </p>
      </div>
    );
  }

  if (loadError && !board) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-950 p-6">
        <i className="ri-error-warning-line text-5xl text-amber-500" />
        <p className="text-center text-white">{loadError}</p>
        <button
          type="button"
          onClick={() => void fetchBoard()}
          className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 cursor-pointer"
        >
          重试
        </button>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-400">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-3xl text-red-400" />
          <span className="text-sm">加载签到看板…</span>
        </div>
      </div>
    );
  }

  const { activity, attendances } = board;
  const rate = activity.enrolled > 0 ? Math.min(100, Math.round((activity.checkedIn / activity.enrolled) * 100)) : 0;

  return (
    <div
      className="min-h-screen flex flex-col text-white"
      style={{ background: "linear-gradient(160deg, #0c1222 0%, #1e293b 45%, #0f172a 100%)" }}
    >
      <header className="flex-shrink-0 border-b border-white/10 px-4 py-4 sm:px-6">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-red-500/30 bg-red-600/15">
              <i className="ri-qr-code-line text-xl text-red-400" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">现场签到码</p>
              <h1 className="truncate text-lg font-bold leading-snug sm:text-xl">{activity.title}</h1>
              <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-400">
                <span>
                  <i className="ri-map-pin-line mr-0.5" />
                  {activity.location}
                </span>
                <span>
                  <i className="ri-time-line mr-0.5" />
                  {activity.eventDate}
                </span>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-300">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
              实时更新
            </span>
            {lastSync && (
              <span className="text-[11px] text-slate-500 tabular-nums">
                同步 {lastSync.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-7xl flex-1 flex-col gap-6 p-4 sm:p-6 lg:flex-row lg:items-stretch lg:gap-8 lg:overflow-hidden">
        {/* 二维码区：手机竖屏在上，横屏/平板在左 */}
        <section className="flex flex-col items-center lg:w-[min(100%,420px)] lg:flex-shrink-0 lg:justify-center">
          <div className="w-full max-w-[400px] rounded-2xl border border-white/10 bg-white/[0.06] p-5 sm:p-6">
            <p className="mb-4 text-center text-xs text-slate-400">
              使用微信小程序扫描 · 图案随服务器时间约每分钟刷新（降低截图盗用风险）
            </p>
            <div className="relative mx-auto w-full max-w-[320px]">
              <div
                className="rounded-2xl p-1"
                style={{ background: "linear-gradient(135deg, #dc2626, #f59e0b, #10b981)" }}
              >
                <div className="flex aspect-square items-center justify-center overflow-hidden rounded-xl bg-white p-3">
                  {qrDataUrl ? (
                    <img src={qrDataUrl} alt="活动签到二维码" className="h-full w-full object-contain" />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-slate-400">
                      <i className="ri-loader-4-line animate-spin text-2xl" />
                      <span className="text-xs">生成二维码…</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-lg">
                  <i className="ri-football-line text-xl text-red-600" />
                </div>
              </div>
            </div>
            <p className="mt-4 text-center text-[11px] leading-relaxed text-slate-500">
              扫码后将打开活动签到页；请确保会员已报名本场活动。
            </p>
          </div>

          <div className="mt-4 grid w-full max-w-[400px] grid-cols-3 gap-2 sm:gap-3">
            {[
              { label: "已报名", value: activity.enrolled },
              { label: "已签到", value: activity.checkedIn, accent: "text-emerald-400" },
              { label: "签到率", value: `${rate}%`, accent: rate >= 80 ? "text-emerald-400" : "text-amber-400" },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-xl border border-white/10 bg-white/5 py-3 text-center"
              >
                <p className={`text-lg font-bold sm:text-xl ${s.accent ?? "text-white"}`}>{s.value}</p>
                <p className="mt-0.5 text-[10px] text-slate-500 sm:text-xs">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 实时签到昵称 */}
        <section className="flex min-h-[min(50vh,420px)] flex-1 flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] lg:min-h-0">
          <div className="flex flex-shrink-0 items-center justify-between border-b border-white/10 px-4 py-3 sm:px-5">
            <h2 className="flex items-center gap-2 text-sm font-bold sm:text-base">
              <i className="ri-user-follow-line text-emerald-400" />
              实时签到
              {activity.checkedIn > 0 && (
                <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-300">
                  {activity.checkedIn} 人
                </span>
              )}
            </h2>
          </div>
          <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-3 sm:px-4 sm:py-4">
            {attendances.length === 0 ? (
              <div className="flex h-full min-h-[200px] flex-col items-center justify-center gap-2 text-slate-500">
                <i className="ri-qr-scan-2-line text-4xl text-slate-600" />
                <p className="text-sm">等待会员扫码签到…</p>
                <p className="text-center text-xs text-slate-600">签到成功后将在此显示昵称</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {attendances.map((rec, idx) => (
                  <li
                    key={`${rec.memberName}-${rec.checkedAt}-${idx}`}
                    className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 sm:px-4 sm:py-3 ${
                      idx === 0
                        ? "border-emerald-500/35 bg-emerald-500/10"
                        : "border-white/10 bg-white/[0.03]"
                    }`}
                  >
                    <div
                      className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white sm:h-10 sm:w-10 sm:text-sm ${AVATAR_HUES[idx % AVATAR_HUES.length]}`}
                    >
                      {rec.memberName.length >= 2 ? rec.memberName.slice(-2) : rec.memberName}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-white sm:text-base">{rec.memberName}</p>
                      {rec.memberNo ? (
                        <p className="truncate font-mono text-[11px] text-slate-500">{rec.memberNo}</p>
                      ) : null}
                    </div>
                    <div className="flex flex-shrink-0 flex-col items-end gap-0.5 text-right">
                      <span className="text-xs text-emerald-400">
                        <i className="ri-checkbox-circle-fill mr-0.5" />
                        已签到
                      </span>
                      <span className="text-[11px] tabular-nums text-slate-500">{rec.checkedAt}</span>
                      {rec.pointsAwarded > 0 ? (
                        <span className="text-[10px] font-semibold text-amber-400">+{rec.pointsAwarded} 积分</span>
                      ) : null}
                    </div>
                    {idx === 0 && (
                      <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-semibold text-white">
                        最新
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>

      <footer className="flex-shrink-0 border-t border-white/5 px-4 py-3 text-center">
        <p className="text-[11px] text-slate-600">
          <i className="ri-shield-check-line mr-1" />
          二维码含活动标识与服务器时间桶，与小程序扫码解析规则一致
        </p>
      </footer>
    </div>
  );
}
